import React, { useState, useEffect, useRef, useMemo } from 'react';
import Modal from '../ui/Modal';
import { updateSoftSchedule } from '../../api/client';
import { buildApiUrl, apiConfig } from '../../apiConfig';

function toUtcIso(dateStr) {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T00:00:00Z`;
}

function toDateInput(isoStr) {
  if (!isoStr) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoStr));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

const INPUT = { padding: '5px 8px', background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 };
const LABEL = { fontWeight: 600, marginBottom: 4, color: '#e3f2fd', fontSize: 13 };

export default function UtilizationSoftScheduleEditModal({ open, onClose, onSuccess, token, entry }) {
  const descriptionRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [hours, setHours] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !entry) return;
    setError(null);
    setSearchUser('');
    // Populate the uncontrolled input directly so it always reflects entry on open
    if (descriptionRef.current) descriptionRef.current.value = entry.projectInfo || '';
    setDateStart(toDateInput(entry.dateStart));
    setDateEnd(toDateInput(entry.dateEnd));
    setHours(entry.hours != null ? String(entry.hours) : '');
    setSelectedUser(entry.memberIdentifier
      ? { identifier: entry.memberIdentifier, name: entry.memberName || entry.memberIdentifier, _preset: true }
      : null);

    let cancelled = false;
    setLoadingUsers(true);
    (async () => {
      try {
        const url = buildApiUrl(apiConfig.cwUsersPath, { conditions: 'inactiveFlag=false' });
        const res = await fetch(url, { headers: { Accept: 'application/json', ...(token ? { Authorization: token } : {}) } });
        const data = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        if (!cancelled) {
          setUsers(list);
          const match = list.find(u => u?.identifier === entry.memberIdentifier || String(u?.id) === entry.memberIdentifier);
          if (!cancelled) setSelectedUser(match ?? { identifier: entry.memberIdentifier, name: entry.memberName || entry.memberIdentifier, _preset: true });
        }
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, entry, token]);

  useEffect(() => {
    if (!open) {
      setUsers([]); setSelectedUser(null); setSearchUser('');
      setDateStart(''); setDateEnd(''); setHours(''); setError(null);
    }
  }, [open]);

  const filteredUsers = useMemo(() => {
    const n = searchUser.trim().toLowerCase();
    if (!n) return users;
    return users.filter(u => {
      const nm = (u?.name || u?.fullName || u?.identifier || '').toLowerCase();
      return nm.includes(n) || String(u?.id ?? '').includes(n);
    });
  }, [users, searchUser]);

  async function onSubmit() {
    try {
      setError(null);
      const memberIdentifier = selectedUser?.identifier || String(selectedUser?.id ?? '');
      if (!memberIdentifier) throw new Error('Select a user');
      if (!dateStart || !dateEnd) throw new Error('Enter start and end dates');
      const ds = toUtcIso(dateStart);
      const de = toUtcIso(dateEnd);
      if (!ds || !de) throw new Error('Invalid dates');
      if (new Date(de) < new Date(ds)) throw new Error('End date must be on or after start date');

      const patch = {
        dateStart: ds,
        dateEnd: de,
        memberIdentifier,
        projectInfo: (descriptionRef.current?.value || '').trim(),
        hours: hours !== '' && Number(hours) > 0 ? Number(hours) : 0,
        ...(entry.ticketId != null ? { ticketId: entry.ticketId } : {}),
        ...(entry.ticketInfo ? { ticketInfo: entry.ticketInfo } : {}),
      };

      setSubmitting(true);
      const result = await updateSoftSchedule(entry.id, patch, token);
      onSuccess?.({ ...entry, ...patch, ...(result ?? {}) });
      onClose?.();
    } catch (e) {
      setError(e);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open || !entry) return null;

  const canSubmit = !submitting && !!selectedUser && !!dateStart && !!dateEnd;

  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, color: '#e3f2fd' }}>Edit Soft Schedule</h3>
        <button onClick={() => onClose?.()} disabled={submitting} style={{ background: 'none', border: 'none', color: '#90caf9', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
      </div>

      <div style={{ display: 'grid', gap: 14, color: '#e3f2fd' }}>
        {error && <div style={{ color: '#ef9a9a', fontSize: 13 }}>⚠ {error.message}</div>}

        {/* Read-only ticket context when present */}
        {entry.ticketInfo && (
          <div style={{ background: '#1a3a3a', border: '1px solid #37474f', borderRadius: 5, padding: '8px 12px', fontSize: 13 }}>
            <div style={{ color: '#80cbc4', fontWeight: 700, marginBottom: 2 }}>Ticket (read-only)</div>
            <div style={{ color: '#e3f2fd' }}>{entry.ticketInfo}</div>
          </div>
        )}

        {/* Label / description stored in projectInfo */}
        <div>
          <div style={LABEL}>Label / Description</div>
          <input
            ref={descriptionRef}
            type="text"
            placeholder="e.g. On-call coverage, Buffer time…"
            defaultValue={entry.projectInfo || ''}
            style={{ ...INPUT, width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {/* Member */}
        <div>
          <div style={LABEL}>Member</div>
          <input
            placeholder="Search by name or ID…"
            value={searchUser}
            onChange={e => setSearchUser(e.target.value)}
            style={{ ...INPUT, width: '100%', boxSizing: 'border-box', marginBottom: 6 }}
          />
          <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}>
            {loadingUsers && <div style={{ padding: '6px 10px', color: '#555', fontSize: 12 }}>Loading users…</div>}
            {!loadingUsers && filteredUsers.slice(0, 300).map(u => {
              const isSelected = selectedUser
                ? (selectedUser._preset ? selectedUser.identifier === u?.identifier : selectedUser.id === u?.id)
                : false;
              const name = u?.name || u?.fullName || u?.identifier || `User ${u?.id}`;
              return (
                <label key={u?.id ?? u?.identifier} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', cursor: 'pointer', background: isSelected ? '#e3f2fd' : 'transparent' }}>
                  <input type="radio" name="util_soft_edit_user" checked={isSelected} onChange={() => setSelectedUser(u)} style={{ cursor: 'pointer' }} />
                  <span style={{ color: '#000', fontSize: 12 }}>{u?.id ? `${u.id} • ` : ''}{name}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Dates + Hours */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={LABEL}>Start date</div>
            <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} style={INPUT} />
          </div>
          <div>
            <div style={LABEL}>End date</div>
            <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} style={INPUT} />
          </div>
          <div>
            <div style={LABEL}>Hours <span style={{ fontWeight: 400, color: '#78909c', fontSize: 11 }}>(optional)</span></div>
            <input
              type="number" min="0" step="0.5" placeholder="e.g. 8"
              value={hours}
              onChange={e => setHours(e.target.value)}
              style={{ ...INPUT, width: 90 }}
            />
          </div>
        </div>

        <button
          onClick={onSubmit}
          disabled={!canSubmit}
          style={{
            padding: '8px 18px',
            background: canSubmit ? '#1565c0' : '#455a64',
            border: 'none', borderRadius: 5,
            color: '#fff', fontWeight: 700, fontSize: 13,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            alignSelf: 'flex-start',
          }}
        >
          {submitting ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </Modal>
  );
}
